import React, { useEffect, useMemo, useState } from 'react';
import StatsCard from '../components/StatsCard/StatsCard';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Chart from "react-apexcharts";
import Loader from "../components/utils/Loader";
import genericActions from "../redux/generic/actions";
import { useDispatch, useSelector } from 'react-redux';
import ToastAlert from "../components/utils/ToastAlert";

const { getDashboardData } = genericActions;

const Dashboard = () => {
  const dispatch = useDispatch();
  const [selectPeriod, setSelectPeriod] = useState(1);
  const { dashboardData, dashboardLoading, dashboardError, } = useSelector((state) => state.generic);
  const activationData = dashboardData?.activationData ?? {};
  const rechargeData = dashboardData?.rechargeData ?? {};
  const inventoryData = dashboardData?.inventoryData ?? {};


  // Activation
  const activationSeries = [activationData?.successData || 0, activationData?.pendingData || 0, activationData?.failedData || 0]; //[success,pending,failed]

  // Recharge
  const rechargeSeries = [rechargeData?.successData || 0, rechargeData?.pendingData || 0, rechargeData?.failedData || 0]; //[success,pending,failed]

  const options = [
    { key: 1, label: "Current Week" },
    { key: 2, label: "Last Week" },
    { key: 3, label: "This Month" },
    { key: 4, label: "This Year" },
  ];


  useEffect(() => {
    const payLoad = {
      actionType: selectPeriod
    }
    dispatch(getDashboardData(payLoad))
  }, []);

  useEffect(() => {
    if (dashboardError) {
      ToastAlert({ type: "error", text: dashboardError });
    }
  }, [dashboardError]);

  const statsCardData = [
    {
      id: 1,
      counter: activationData?.totalData || 0,
      bgColor: '#5c6bc0',
      symbol: '',
      symbolPosition: 'right',
      isCounter: true,
      title: 'Activation',
      progressPercent: 25,
      description: 'Total number of activated services.',
    },
    {
      id: 2,
      counter: rechargeData?.totalData || 0,
      bgColor: '#ffa726',
      symbol: '',
      symbolPosition: '',
      isCounter: true,
      title: 'Recharge',
      progressPercent: 25,
      description: 'Total number of recharge transactions completed.',
    },
    {
      id: 3,
      counter: inventoryData?.totalData || 0,
      bgColor: '#ef5350',
      symbol: '',
      symbolPosition: 'left',
      isCounter: true,
      title: 'Inventory Status',
      progressPercent: 25,
      description: 'Current stock availability across all items.',
    },
    // {
    //   id: 4,
    //   counter: 578,
    //   bgColor: '#42a5f5',
    //   symbol: '$',
    //   symbolPosition: 'left',
    //   isCounter: true,
    //   title: 'Profit',
    //   progressPercent: 25,
    //   description: 'Total profit generated from sales and services.',
    // }
  ];

  const changeDateHandler = (item) => {
    setSelectPeriod(item.key)
    const payLoad = {
      actionType: item.key
    }
    dispatch(getDashboardData(payLoad))
  }

  const donutOptions = (title) => ({
    chart: { type: "donut" },
    labels: ["Success", "Pending", "Fail"],
    colors: ["#008ffb", "#53b3fc", "#8accfd"],
    //  colors: ["#28a745", "#dc3545", "#ffc107"],
    legend: { position: "bottom" },
    // title: { text: title, align: "center" },
  });


  const getDateCategories = (type) => {
    const now = new Date();

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    let labels = [];

    switch (type) {

      // Current Week (Mon → Sun)
      case 1: {
        const day = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - day + (day === 0 ? -6 : 1));

        for (let i = 0; i < 7; i++) {
          const d = new Date(monday);
          d.setDate(monday.getDate() + i);
          labels.push(d.toLocaleDateString("en-US", { weekday: "short" }));
        }
        break;
      }

      // Last Week → Day wise
      case 2: {
        const day = now.getDay();
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - day - 6);

        for (let i = 0; i < 7; i++) {
          const d = new Date(lastMonday);
          d.setDate(lastMonday.getDate() + i);
          labels.push(d.toLocaleDateString("en-US", { weekday: "short" }));
        }
        break;
      }

      // This Month → Week wise
      case 3: {
        const year = now.getFullYear();
        const month = now.getMonth();
        const totalDays = new Date(year, month + 1, 0).getDate();
        const weeks = Math.ceil(totalDays / 7);

        for (let i = 1; i <= weeks; i++) {
          labels.push(`Week ${i}`);
        }

        break;
      }

      // This Year → Month wise
      case 4: {
        let d = new Date();
        d.setDate(1);

        for (let i = 0; i < 12; i++) {
          labels.unshift(
            `${monthNames[d.getMonth()]} ${d.getFullYear()}`
          );
          d.setMonth(d.getMonth() - 1);
        }
        break;
      }

      default:
        return [];
    }

    return labels;
  };


  const generateSeriesData = (length) => {
    return Array.from({ length }, () =>
      Math.floor(Math.random() * 1000) + 100
    );
  };

  const commissionCategories = useMemo(() => {
    return getDateCategories(selectPeriod);
  }, [selectPeriod]);


  const commissionSeries = useMemo(() => {
    return [
      {
        name: "Commission",
        data: generateSeriesData(commissionCategories.length),
      },
    ];
  }, [commissionCategories]);


  const lineOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    colors: ["#8e0e9d"],
    xaxis: {
      categories: commissionCategories,
    },
    yaxis: {
      labels: {
        formatter: (val) => `$${val}`,
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `$${val}`,
      },
    },
  };




  return (
    <>
      {/* <Loader/> */}
      <Container fluid className="p-0">

        {/* Tenure Tabs */}
        <div className="mb-3">

          {/* Buttons */}
          <div className="d-flex gap-3 flex-wrap">
            {options.map((item) => (
              <div
                key={item.key}
                onClick={() => changeDateHandler(item)}
                className="d-flex align-items-center px-3 py-2 rounded border"
                style={{
                  cursor: "pointer",
                  minWidth: "150px",
                  justifyContent: "space-between",
                  background:
                    selectPeriod === item.key
                      ? "linear-gradient(90deg, #8e0e9d, #8e0e9d)"
                      : "#fff",
                  color: selectPeriod === item.key ? "#fff" : "#000",
                  borderColor: selectPeriod === item.key ? "transparent" : "#dee2e6",
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  <span style={{ fontWeight: "500", fontSize: "14px" }}>{item.label}</span>
                </div>

                {item.isDefault && selectPeriod === item.key && (
                  <span className="badge bg-light text-primary ms-2">
                    Default
                  </span>
                )}
              </div>
            ))}
          </div>

        </div>
        {!dashboardLoading ? <>
          {/* start revenue section */}
          <Row className="gy-3 mb-4">
            {statsCardData.map((cardItem) => (
              <Col key={cardItem?.id} md={4} lg={4}>
                <StatsCard
                  type="revenue-progressBar"
                  bgColor={cardItem?.bgColor}
                  symbol={cardItem?.symbol}
                  symbolPosition={cardItem?.symbolPosition}
                  counter={cardItem?.counter}
                  isCounter={cardItem?.isCounter}
                  title={cardItem?.title}
                  progressPercent={cardItem?.progressPercent}
                  description={cardItem?.description}
                />
              </Col>
            ))}
          </Row>
          {/* end revenue section */}

          {/* Grap cards*/}
          <div className="row g-3">
            {/* Activation */}
            <div className="col-md-6 d-flex">
              <div className="p-3 w-100 h-100" style={{ backgroundColor: '#fff' }}>
                <div className="card-body d-flex flex-column">
                  <h6 className="r"><i className="ri-checkbox-circle-line me-2"></i>Activation</h6>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <Chart
                      options={donutOptions("Activation")}
                      series={activationSeries}
                      type="donut"
                      height={300}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recharge */}
            <div className="col-md-6 d-flex">
              <div className="p-3 w-100 h-100" style={{ backgroundColor: '#fff' }}>
                <div className="card-body d-flex flex-column">
                  <h6 className=""><i className="ri-flashlight-line me-2"></i>Recharge</h6>

                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <Chart
                      options={donutOptions("Recharge")}
                      series={rechargeSeries}
                      type="donut"
                      height={300}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Commission */}
            {/* <div className="col-md-4 d-flex">
            <div className="p-3 w-100 h-100" style={{ backgroundColor: '#fff' }}>
              <div className="card-body d-flex flex-column">

                <h6 className="mb-3">
                  <i className="ri-stack-line me-2"></i>
                  Commission
                </h6>

                <div className="flex-grow-1">
                  <Chart
                    options={lineOptions}
                    series={commissionSeries}
                    type="line"
                    height={270}
                    width="100%"
                  />
                </div>

              </div>
            </div>
          </div> */}

          </div>
        </> : <div className='mt-5 d-flex align-items-center justify-content-center' style={{ height: '50vh' }}><Loader /></div>}
      </Container>
    </>
  )
}

export default Dashboard
